import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquareHeart, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TripAutoHero from "../components/TripAutoHero";

/* Site-wide feedback form: Comment / Complaint / Suggestion / Appreciation / Other. */

const TYPES = [
  { id: "comment", label: "Comment" },
  { id: "complaint", label: "Complaint" },
  { id: "suggestion", label: "Suggestion" },
  { id: "appreciation", label: "Appreciation" },
  { id: "other", label: "Other — specify" },
];

export default function Feedback() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [type, setType] = useState("comment");
  const [otherType, setOtherType] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sourcePage, setSourcePage] = useState(params.get("from") || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/feedback", {
        type, other_type: type === "other" ? otherType : null,
        name, email, phone, message,
        page_url: sourcePage || null,
      });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check all fields and try again");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="feedback-done">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
        <h1 className="font-heading font-900 text-3xl">Thank you!</h1>
        <p className="text-zinc-600 mt-3">Your {TYPES.find((t) => t.id === type)?.label.toLowerCase().replace(" — specify", "")} has been received. We read every message and it truly helps us build a better TravelerPulse.</p>
      </div>
    );
  }

  return (
    <div data-testid="feedback-page">
      <div className="relative border-b border-zinc-200 overflow-hidden">
        <TripAutoHero query="postcard conversation feedback letters" alt="Share your feedback" className="absolute inset-0 w-full h-full" testid="feedback-hero" />
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-900/85 via-teal-700/70 to-blue-600/55" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-200 mb-2 flex items-center gap-2"><MessageSquareHeart className="w-4 h-4" /> We're listening</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight text-white">Share Your Thoughts</h1>
          <p className="text-white/90 mt-3 max-w-2xl">A comment, a complaint, a suggestion or just appreciation — every message helps us improve TravelerPulse for travelers everywhere.</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 lg:px-8 py-12">
      <form onSubmit={submit} className="border border-zinc-300 bg-white p-6 space-y-4">
        <div>
          <label className="block text-sm font-700 mb-1">What would you like to share? *</label>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="feedback-type-select"
            className="w-full border border-zinc-300 px-3 py-2.5 text-sm bg-white outline-none focus:ring-2 focus:ring-primary">
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        {type === "other" && (
          <div>
            <label className="block text-sm font-700 mb-1">Please specify *</label>
            <input value={otherType} onChange={(e) => setOtherType(e.target.value)} required data-testid="feedback-other-input"
              placeholder="What is this about?"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-700 mb-1">Your name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="feedback-name-input"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="block text-sm font-700 mb-1">Phone number *</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} required type="tel" data-testid="feedback-phone-input"
              placeholder="+1 555 123 4567"
              className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-700 mb-1">Email *</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" data-testid="feedback-email-input"
            className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <div>
          <label className="block text-sm font-700 mb-1">Which page is this about? <span className="font-400 text-muted-foreground">(optional — helps us find a fix faster)</span></label>
          <input value={sourcePage} onChange={(e) => setSourcePage(e.target.value)} data-testid="feedback-source-input"
            placeholder="/airports/ATL, My Travel Hub, homepage…"
            className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <div>
          <label className="block text-sm font-700 mb-1">Your message *</label>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} required rows={5} data-testid="feedback-message-input"
            placeholder="Tell us more…"
            className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary resize-y" />
        </div>
        <button type="submit" disabled={busy} data-testid="feedback-submit"
          className="inline-flex items-center gap-2 bg-emerald-700 text-white font-700 px-6 py-3 text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors">
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send feedback"}
        </button>
        <p className="text-xs text-muted-foreground">We use your contact details only to follow up on your message.</p>
      </form>
      </div>
    </div>
  );
}
